import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import { resendClientConsentInvite } from '@/lib/api/clients';
import { isClientConsentAccessBlockedMessage } from '@/lib/clientConsentAccess';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import axios from 'axios';

interface ErrorStateProps {
  error: string;
  clientId?: string | null;
}

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_COOLDOWN_STORAGE_PREFIX = 'caloriescience_client_consent_resend_available_at';

const getCooldownStorageKey = (clientId: string): string =>
  `${RESEND_COOLDOWN_STORAGE_PREFIX}:${clientId}`;

const readCooldownSeconds = (clientId: string): number => {
  if (typeof window === 'undefined' || !clientId) {
    return 0;
  }

  const raw = window.localStorage.getItem(getCooldownStorageKey(clientId));
  const availableAt = raw ? Number(raw) : 0;
  if (!Number.isFinite(availableAt)) {
    return 0;
  }

  return Math.max(0, Math.ceil((availableAt - Date.now()) / 1000));
};

const persistCooldown = (clientId: string, seconds: number) => {
  if (typeof window === 'undefined' || !clientId || seconds <= 0) {
    return;
  }

  window.localStorage.setItem(
    getCooldownStorageKey(clientId),
    String(Date.now() + seconds * 1000)
  );
};

const getRetryAfterSeconds = (error: unknown): number => {
  if (!axios.isAxiosError(error)) {
    return 0;
  }

  const data = error.response?.data as {
    retryAfterSeconds?: unknown;
    resendAvailableInSeconds?: unknown;
  } | undefined;

  const retryAfterSeconds = Number(data?.retryAfterSeconds ?? data?.resendAvailableInSeconds);
  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.ceil(retryAfterSeconds)
    : 0;
};

export const ErrorState: React.FC<ErrorStateProps> = ({ error, clientId }) => {
  const normalizedClientId = clientId?.trim() || '';
  const canResendConsent = useMemo(
    () => isClientConsentAccessBlockedMessage(error),
    [error]
  );
  const [isResendingConsent, setIsResendingConsent] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(() =>
    normalizedClientId ? readCooldownSeconds(normalizedClientId) : 0
  );

  useEffect(() => {
    if (!normalizedClientId || !canResendConsent) {
      setResendCooldownSeconds(0);
      return;
    }

    setResendCooldownSeconds(readCooldownSeconds(normalizedClientId));
  }, [canResendConsent, normalizedClientId]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setResendCooldownSeconds(normalizedClientId ? readCooldownSeconds(normalizedClientId) : 0);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [normalizedClientId, resendCooldownSeconds]);

  const handleResendConsentEmail = async () => {
    if (!normalizedClientId) {
      showWarningToast('Client unavailable', 'Could not determine which client needs consent.');
      return;
    }
    if (isResendingConsent || resendCooldownSeconds > 0) {
      return;
    }

    setIsResendingConsent(true);
    try {
      const response = await resendClientConsentInvite(normalizedClientId);
      const responseCooldown = Number(response.resendAvailableInSeconds ?? 0);
      const cooldown = response.inviteEmailSent
        ? Math.max(RESEND_COOLDOWN_SECONDS, responseCooldown)
        : Math.max(0, responseCooldown);
      if (cooldown > 0) {
        persistCooldown(normalizedClientId, cooldown);
        setResendCooldownSeconds(cooldown);
      }

      if (response.inviteEmailSent) {
        showSuccessToast(
          'Consent email sent',
          'Ask the client to check their email and use the latest consent link.'
        );
        return;
      }

      showWarningToast(
        'Consent email not sent',
        response.inviteEmailError || response.message || 'The consent token was refreshed, but email delivery failed.'
      );
    } catch (resendError) {
      const retryAfterSeconds = getRetryAfterSeconds(resendError);
      if (retryAfterSeconds > 0) {
        persistCooldown(normalizedClientId, retryAfterSeconds);
        setResendCooldownSeconds(retryAfterSeconds);
      }
      showErrorToast(resendError, 'Could not resend consent email');
    } finally {
      setIsResendingConsent(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top,_rgba(32,174,220,0.16),_transparent_34rem)]">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col font-manrope px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center">
          <Link to="/clients">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 rounded-md px-2 text-[#20AEDC] hover:bg-cyan-50 hover:text-[#159fc9]"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Clients
            </Button>
          </Link>
        </div>
        <div className="flex flex-1 items-start justify-center pt-20 sm:pt-28">
          <div className="w-full max-w-2xl text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-cyan-100 bg-white text-[#20AEDC] shadow-sm">
              <AlertCircle className="h-7 w-7" />
            </div>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]">
              Error Loading Client
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-base leading-7 text-slate-500 sm:text-lg">
              {error}
            </p>
          {canResendConsent ? (
            <div className="mx-auto mt-7 max-w-[620px] rounded-lg border border-slate-200 bg-white p-5 text-left shadow-[0_18px_44px_rgba(15,23,42,0.08)] sm:p-6">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-cyan-50 text-[#20AEDC]">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-slate-900">
                    Client consent is pending
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Resend the consent email, then ask the client to open the latest link from their inbox.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
                <Button
                  type="button"
                  onClick={handleResendConsentEmail}
                  disabled={!normalizedClientId || isResendingConsent || resendCooldownSeconds > 0}
                  className="h-11 gap-2 rounded-md bg-[#20AEDC] px-5 text-sm font-semibold text-white shadow-none hover:bg-[#159fc9] disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${isResendingConsent ? 'animate-spin' : ''}`} />
                  {isResendingConsent
                    ? 'Sending...'
                    : resendCooldownSeconds > 0
                      ? `Resend in ${resendCooldownSeconds}s`
                      : 'Resend consent email'}
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="h-11 rounded-md border-[#20AEDC] px-5 text-sm font-semibold text-[#20AEDC] hover:bg-cyan-50 hover:text-[#159fc9]"
                >
                  Try Again
                </Button>
              </div>
              {!normalizedClientId ? (
                <p className="mt-3 text-center text-xs text-amber-700">
                  Open this client from the clients list to resend consent.
                </p>
              ) : null}
            </div>
          ) : (
            <Button
              onClick={() => window.location.reload()}
              className="mt-6 h-11 rounded-md border-[#20AEDC] px-5 text-sm font-semibold text-[#20AEDC] hover:bg-cyan-50 hover:text-[#159fc9]"
              variant="outline"
            >
              Try Again
            </Button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};
